import { Flex, Box, Button, Badge, Modal } from '@mantine/core';
import {
  IconChevronLeft,
  IconUsers,
  IconPlus,
  IconDots,
  IconDoorEnter,
  IconTrash,
  IconMailUp,
  IconCodeAsterix,
} from '@tabler/icons-react';
import ModalConfirmation from 'components/ModalConfirmation';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HOUSE_PAGE_PATH,
  USER_ADD_PAGE_PATH,
  USER_PIN_CODE_PAGE_PATH,
  USER_TEMPORARY_PIN_CODE_PAGE_PATH,
} from 'routes/paths';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Medium from 'theme/components/Typography/P1Medium';
import P2Medium from 'theme/components/Typography/P2Medium';
import P2Regular from 'theme/components/Typography/P2Regular';
import { useApi } from 'api/api-context';
import { GetCurrentUserResponse } from 'api/actions/get-current-user/get-current-user-response';
import panic from 'panic';

/**
 * Users list page - Household users management
 */
const Users: React.FC = () => {
  const { _t, lang } = useLang();
  const navigate = useNavigate();
  const { getAction, buildingId } = useApi();
  const [modalOpened, setModalOpened] = useState(false);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<
    Array<{
      id: number;
      fullName: string;
      email: string;
      passwordSet: boolean;
      parent: boolean;
    }>
  >([]);
  const [familyInfo, setFamilyInfo] = useState<{
    isParent: boolean;
    limit: number;
    currentSize: number;
    remainingSlots: number;
  } | null>(null);
  const [currentBuilding, setCurrentBuilding] = useState<NonNullable<GetCurrentUserResponse['buildings']>[0] | null>(
    null
  );
  const selectedUser = users.find((user) => user.id === selectedUserId) ?? null;

  /**
   * Load current user and family members from API
   */
  useEffect(() => {
    const getCurrentUser = getAction('GetCurrentUser');
    const getFamily = getAction('GetFamily');

    Promise.all([getCurrentUser({}), getFamily({})])
      .then(([currentUserResponse, familyResponse]) => {
        const buildings = currentUserResponse.buildings || [];

        // Set current user ID
        setCurrentUserId(currentUserResponse.user?.id || null);
        setCurrentBuilding(buildings.find((building) => building.id === buildingId) || null);

        // Set family members
        const members = (familyResponse.members || []).map((member) => ({
          id: member.id || 0,
          fullName: member.fullName || '',
          email: member.email || '',
          passwordSet: member.passwordSet || false,
          parent: member.parent || false,
        }));
        setUsers(members);

        // Set family info
        setFamilyInfo({
          isParent: familyResponse.isParent || false,
          limit: familyResponse.limit || 6,
          currentSize: familyResponse.currentSize || 0,
          remainingSlots: familyResponse.remainingSlots || 0,
        });
      })
      .catch(panic);
  }, [buildingId, getAction]);

  /**
   * Opens the user options modal for a specific user
   * @param userId - The ID of the user to manage
   */
  const handleOpenModal = (userId: number) => {
    setSelectedUserId(userId);
    setModalOpened(true);
  };

  /**
   * Closes the user options modal
   */
  const handleCloseModal = () => {
    setModalOpened(false);
  };

  /**
   * Opens the PIN management page for the selected family member
   */
  const handleManagePinCode = () => {
    if (!selectedUserId) return;

    navigate(USER_PIN_CODE_PAGE_PATH.insert({ userId: selectedUserId }));
    setModalOpened(false);
  };

  /**
   * Opens the temporary PIN management page for the selected family member.
   */
  const handleManageTemporaryPinCodes = () => {
    if (!selectedUserId) return;

    navigate(USER_TEMPORARY_PIN_CODE_PAGE_PATH.insert({ userId: selectedUserId }));
    setModalOpened(false);
  };

  /**
   * Opens the confirmation modal for user deletion
   */
  const handleOpenConfirmationModal = () => {
    setModalOpened(false);
    setConfirmationModalOpened(true);
  };

  /**
   * Closes the confirmation modal
   */
  const handleCloseConfirmationModal = () => {
    setConfirmationModalOpened(false);
  };

  /**
   * Resends the invitation email to the selected user
   */
  const handleResendInvitation = () => {
    if (!selectedUserId) return;

    setLoading(true);
    const sendFamilyInviteEmail = getAction('SendFamilyInviteEmail');

    sendFamilyInviteEmail({
      parameters: {
        userId: selectedUserId,
        lang: lang as 'sk' | 'en' | 'cs',
      },
    })
      .then(() => {
        setModalOpened(false);
        // TODO: Show success notification
      })
      .catch((error) => {
        // TODO: Show error notification
        // console.error('Failed to send invitation:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Deletes the selected user from the household
   */
  const handleDeleteUser = () => {
    if (!selectedUserId) return;
    setLoading(true);
    const deleteFamilyMember = getAction('DeleteFamilyMember');
    deleteFamilyMember({ parameters: { userId: selectedUserId } })
      .then(() => {
        // Reload family data
        const getFamily = getAction('GetFamily');
        return getFamily({});
      })
      .then((response) => {
        const members = (response.members || []).map((member) => ({
          id: member.id || 0,
          fullName: member.fullName || '',
          email: member.email || '',
          passwordSet: member.passwordSet || false,
          parent: member.parent || false,
        }));
        setUsers(members);
        setConfirmationModalOpened(false);
        setModalOpened(false);
        setSelectedUserId(null);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  };

  /**
   * Returns the appropriate status badge component based on user passwordSet status
   * @param passwordSet - Whether the user has set their password
   * @returns A Badge component with the appropriate styling and text
   */
  const getStatusBadge = (passwordSet: boolean) => {
    if (passwordSet) {
      return (
        <Badge color="#1B8F34" variant="light" size="lg" radius="md">
          {_t('Activated')}
        </Badge>
      );
    }
    return (
      <Badge color="#8691A1" variant="light" size="lg" radius="md">
        {_t('Invited')}
      </Badge>
    );
  };

  return (
    <SimpleLayout>
      <Box p="1rem">
        <Box mb="1rem">
          <Flex>
            <Link
              to={HOUSE_PAGE_PATH.original}
              style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
            >
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back to my household')}</P2Medium>
            </Link>
          </Flex>
          <Flex gap="0.5rem" align="center">
            <IconUsers size="2rem" color={theme.colors?.gray?.[12]} />
            <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('List of users')}</H1SemiBold>
          </Flex>
          <P2Medium color={theme.colors?.gray?.[6]} mt="0.25rem">
            {_t('Max. 6 users per household.')}
          </P2Medium>
        </Box>

        {/* Users list */}
        <Flex direction="column" gap="0.75rem" pb="4rem">
          {users.map((user) => (
            <Box
              key={user.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '1rem',
              }}
            >
              <Flex justify="space-between" align="center">
                <Box>
                  <H2SemiBold color={theme.colors?.gray?.[12]} style={{ fontWeight: 600 }}>
                    {user.fullName}
                  </H2SemiBold>
                  <P2Regular color={theme.colors?.gray?.[6]} mt="0.125rem">
                    {user.email}
                  </P2Regular>
                  <Flex gap="0.5rem" mt="0.25rem">
                    {user.id === currentUserId && (
                      <Badge color="#551FFF" variant="light" size="lg" radius="md">
                        {_t('My account')}
                      </Badge>
                    )}
                    {getStatusBadge(user.passwordSet)}
                  </Flex>
                </Box>
                {familyInfo?.isParent && user.id !== currentUserId && (
                  <Button
                    variant="subtle"
                    size="xs"
                    aria-label={`${_t('User options')} ${user.fullName}`}
                    style={{ padding: '0.25rem' }}
                    onClick={() => handleOpenModal(user.id)}
                  >
                    <IconDots size="1.25rem" color={theme.colors?.gray?.[6]} />
                  </Button>
                )}
              </Flex>
            </Box>
          ))}
        </Flex>

        {/* Add user button - only visible for parent */}
        {familyInfo?.isParent && (
          <Button
            size="md"
            leftSection={<IconPlus size="1.25rem" />}
            onClick={() => navigate(USER_ADD_PAGE_PATH.original)}
            style={{
              position: 'fixed',
              bottom: '2.5rem',
              right: '1rem',
              boxShadow: '0px 6px 12px 0px rgba(85, 31, 255, 0.30)',
            }}
          >
            {_t('Add user')}
          </Button>
        )}
      </Box>

      <Modal
        variant="secondary"
        opened={modalOpened}
        onClose={handleCloseModal}
        title={_t('User options')}
        transitionProps={{ transition: 'slide-up', duration: 400 }}
      >
        <Box>
          <Box>
            {currentBuilding?.showPinCode && selectedUser && selectedUser.id !== currentUserId && (
              <>
                <Flex gap="0.5rem" onClick={handleManagePinCode} style={{ cursor: 'pointer' }} mb="0.75rem">
                  <IconCodeAsterix />
                  <P1Medium>{_t('Manage PIN code')}</P1Medium>
                </Flex>
                <Flex gap="0.5rem" onClick={handleManageTemporaryPinCodes} style={{ cursor: 'pointer' }} mb="0.75rem">
                  <IconDoorEnter />
                  <P1Medium>{_t('Temporary entries')}</P1Medium>
                </Flex>
              </>
            )}
            <Flex gap="0.5rem" onClick={handleResendInvitation} style={{ cursor: 'pointer' }}>
              <IconMailUp />
              <P1Medium>{_t('Resend invitation')}</P1Medium>
            </Flex>
            <Flex gap="0.5rem" onClick={handleOpenConfirmationModal} style={{ cursor: 'pointer', color: 'red' }}>
              <IconTrash color="red" />
              <P1Medium>{_t('Delete user')}</P1Medium>
            </Flex>
          </Box>
        </Box>
      </Modal>

      <ModalConfirmation
        requestModalOpened={confirmationModalOpened}
        handleCloseRequestModal={handleCloseConfirmationModal}
        handleSubmit={handleDeleteUser}
        title={_t('Do you really want to delete this user?')}
        cancelButtonText={_t('Cancel')}
        submitButtonText={_t('Yes, delete')}
        loading={loading}
      />
    </SimpleLayout>
  );
};

export default Users;
